import 'src/screen/Protected/Wizards/Wizards.scss';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map } from 'immutable';
import { Helmet } from 'react-helmet';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircleOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Button from 'src/component/UI/Button';
import ImportForm from 'src/component/UI/ImportForm';

import { fetchIntegrations } from 'src/module/integration/action';
import { fetchDevicesInAlarmWithoutNotification, fetchAlarmStats } from 'src/module/alarm/action';
import { getIntegrations } from 'src/module/integration/selector';
import { getCriticalAlarmsWithoutNotification, getAlarmHistoricalSummary } from 'src/module/alarm/selector';
import { getPluralWorkTicketLabel } from 'src/module/authentication/selector';
import { getGuide } from 'src/module/facility/selector';
import { importData } from 'src/module/import/action';

import UploadStatusTable from 'src/component/UI/UploadStatusTable';
import { isWhitelabel, useAlarmReports, useEmail } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';


export default function GuideScreen () {
  const { t, tPlural } = useTranslation();
  const guide = useSelector(getGuide);
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);
  const alarmHistory = useSelector(getAlarmHistoricalSummary) as any;
  const integrations = useSelector(getIntegrations);
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchDevicesInAlarmWithoutNotification(null));
    dispatch(fetchAlarmStats(null));
  }, [guide]);

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  const [useAlarmReportsValue, setUseAlarmReportsValue] = useState<boolean | null>(null);
  const [useAlarmReportsLoading, setUseAlarmReportsLoading] = useState(true);
  const [useEmailValue, setUseEmailValue] = useState<boolean | null>(null);

  useEffect(() => {
    async function loadData () {
      const [alarmReportsResult, emailResult] = await Promise.all([useAlarmReports(), useEmail()]);
      setUseAlarmReportsValue(alarmReportsResult);
      setUseEmailValue(emailResult);
      setUseAlarmReportsLoading(false);
    }
    loadData();
  }, []);


  const totalAlarmsAutoHandled = alarmHistory.getIn([alarmHistory.size - 1, 'total_auto_handled'], 'N/A');

  const [sectionCollapsed, setSectionCollapsed] = useState(Map({
    supervisorAlarmData: false,
    erpData: true,
    otherData: true,
    validateAlarms: true,
    sendReport: criticalAlarmsWithoutNotification !== 0,
  }));

  const uploadData: any = {
    supervisorAlarmData: [],
    erpData: [],
    otherData: []
  };

  const uploads = guide.get('uploads', Map());

  uploads.keySeq().forEach((key) => {
    uploadData[uploads.getIn([key, 'importType'], 'otherData')].push({ name: uploads.getIn([key, 'file', 'name']), status: uploads.getIn([key, 'status']) });
  });

  const allUploads = [];
  Object.values(uploadData)?.map((data: any) => data?.map(rec => allUploads.push(rec)));

  const makeToggleSection = (section) => {
    return () => {
      setSectionCollapsed(sectionCollapsed.set(section, !sectionCollapsed.get(section)));
    };
  };
  useEffect(() => {
    if (criticalAlarmsWithoutNotification === 0  && sectionCollapsed.get('sendReport')) {
      setSectionCollapsed(sectionCollapsed.set('sendReport', false));
    } else if (criticalAlarmsWithoutNotification !== 0 && !sectionCollapsed.get('sendReport')) {
      setSectionCollapsed(sectionCollapsed.set('sendReport', true));
    }
  }, [criticalAlarmsWithoutNotification]);

  const isUploadStepComplete = (importType, subType=null) => {
    let completed = false;

    uploads.keySeq().forEach((key) => {
      if (importType === uploads.getIn([key, 'importType'])) {
        if ((subType && subType === uploads.getIn([key, 'subType'])) || !subType) {
          if (uploads.getIn([key, 'status']) === 'success') {
            completed = true;
          }
        }
      }
    });

    return completed;
  };

  const isSupervisorDataComplete = () => {
    const integrationTypes = integrations.reduce((acc, integration) => {
      acc.push(integration.get('type'));
      return acc;
    }, []);

    const checks = [];

    if (integrationTypes.indexOf('raychem') !== -1 || integrationTypes.indexOf('dts') !== -1) {
      checks.push(isUploadStepComplete('supervisorAlarmData', 'dts'));
    }

    if (integrationTypes.indexOf('tvne') !== -1) {
      checks.push(isUploadStepComplete('supervisorAlarmData', 'tvne'));
    }

    return checks.reduce((acc, check) => check && acc, true);
  };

  const completedSteps = {
    supervisorAlarmData: isSupervisorDataComplete(),
    erpData: isUploadStepComplete('erpData'),
    otherData: isUploadStepComplete('otherData'),
    validateAlarms: (criticalAlarmsWithoutNotification === 0),
  };

  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';
  return (
    <div className='page guide-wizard' id='guide'>
      <Helmet>
        <title>{title} - {!useAlarmReportsLoading && useAlarmReportsValue ? t('guide.buildAlarmReport') : t('guide.alarmReport')}</title>
      </Helmet>

      <Grid container spacing={2}>

        <Grid item xs={12}>
          {!useAlarmReportsLoading && <h2>{useAlarmReportsValue ? t('guide.buildAlarmReport') : t('guide.alarmReport')}</h2>}
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <h2 className={classNames('expandable', sectionCollapsed.get('supervisorAlarmData') ? 'collapsed' : 'expanded')} onClick={makeToggleSection('supervisorAlarmData')}>
              {completedSteps['supervisorAlarmData'] ? (<CheckCircleOutlinedIcon className='complete' />) : (<CircleOutlinedIcon />)}
              <span>{t('guide.step1Title')}&nbsp;
                <Tooltip title={t('guide.step1Tooltip', { workTicketLabel: pluralWorkTicketLabel || t('workTickets.title') })}>
                  <QuestionMarkIcon
                    className="questionMarkIcon"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </span>
              <span className='expand-arrow'>{sectionCollapsed.get('supervisorAlarmData') ? '▲' : '▼'}</span>
            </h2>
            { !sectionCollapsed.get('supervisorAlarmData') ? <>
              <ImportForm  importType='supervisorAlarmData' importAction={importData} />
              <UploadStatusTable uploadData={allUploads} />
            </> : null }
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <h2 className={classNames('expandable', sectionCollapsed.get('validateAlarms') ? 'collapsed' : 'expanded')} onClick={makeToggleSection('validateAlarms')}>
              {completedSteps['validateAlarms'] ? (<CheckCircleOutlinedIcon className='complete' />) : (<CircleOutlinedIcon />)}
              <span>{t('guide.step2Title')}&nbsp;</span>
              <span className='expand-arrow'>{sectionCollapsed.get('validateAlarms') ? '▲' : '▼'}</span>
            </h2>
            { !sectionCollapsed.get('validateAlarms') ? (
              <div className='inner-section'>
                <p>{tPlural('guide.step2Description', criticalAlarmsWithoutNotification)}</p>
                <Button cta to='/maintenance'>{t('guide.validateAlarmsButton')}</Button>
              </div>
            ) : null }
          </Paper>
        </Grid>

        {!useAlarmReportsLoading && useAlarmReportsValue && useEmailValue && (
          <Grid item xs={12}>
            <Paper>
              <h2 className={classNames('expandable', sectionCollapsed.get('sendReport') ? 'collapsed' : 'expanded')} onClick={makeToggleSection('sendReport')}>
                <CircleOutlinedIcon />
                <span>{t('guide.step3Title')}&nbsp;</span>
                <span className='expand-arrow'>{sectionCollapsed.get('sendReport') ? '▲' : '▼'}</span>
              </h2>
              { !sectionCollapsed.get('sendReport') ? (
                <div className='inner-section'>
                  <p>{tPlural('guide.step3Description', totalAlarmsAutoHandled)}</p>
                  <p>{t('guide.step3Instructions')}</p>
                  <Button cta to='/report/send'>{t('guide.previewAndSend')}</Button>
                </div>
              ) : null }
            </Paper>
          </Grid>
        )}
      </Grid>

    </div>
  );
}
